import { useState } from "react";
import { useAuthStore } from "@/store/authStore";
import { sfxClick } from "@/utils/soundEngine";
import clsx from "clsx";

/* ── Animated checkmark ────────────────────────────────────────────────────── */
function CheckIcon() {
  return (
    <svg width="28" height="28" viewBox="0 0 56 56" fill="none">
      <circle cx="28" cy="28" r="27" stroke="#8b5cf6" strokeWidth="2" strokeDasharray="170" strokeDashoffset="0"
        style={{ animation: "fl-ring 0.6s ease forwards" }} />
      <polyline points="16,28 24,36 40,20" stroke="#a78bfa" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"
        strokeDasharray="40" strokeDashoffset="40"
        style={{ animation: "fl-check 0.4s 0.5s ease forwards" }} />
      <style>{`
        @keyframes fl-ring  { from { stroke-dashoffset: 170; } to { stroke-dashoffset: 0; } }
        @keyframes fl-check { from { stroke-dashoffset: 40;  } to { stroke-dashoffset: 0; } }
      `}</style>
    </svg>
  );
}

/* ── Data row ───────────────────────────────────────────────────────────────── */
function DataRow({ label, value, warning }) {
  return (
    <div className={clsx(
      "flex items-center justify-between px-4 py-3 rounded-xl border",
      warning
        ? "bg-amber-950/30 border-amber-800/40"
        : "bg-nexus-s2 border-nexus-border"
    )}>
      <span className="text-xs font-bold text-gray-500">{label}</span>
      <span className={clsx("text-sm font-black", warning ? "text-amber-300" : "text-white")}>{value}</span>
    </div>
  );
}

/* ── Main component ─────────────────────────────────────────────────────────── */
export default function FirstLoginModal() {
  const { user, clearFirstLogin } = useAuthStore();
  const [confirmed, setConfirmed] = useState(false);
  const [checked, setChecked] = useState(false);

  const handleConfirm = () => {
    if (!checked) return;
    sfxClick();
    setConfirmed(true);
    // Small delay so the success animation plays before closing
    setTimeout(() => clearFirstLogin(), 1200);
  };

  const roleLabel = {
    student: "Schüler",
    teacher: "Lehrperson",
    mentor:  "Mentor",
    parent:  "Elternteil",
    admin:   "Administrator",
  }[user?.role] || user?.role;

  return (
    <div className="fixed inset-0 z-[200] flex items-end sm:items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.85)", backdropFilter: "blur(12px)" }}
    >
      {/* Ambient glow */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full opacity-10"
          style={{ background: "radial-gradient(circle, #7c3aed 0%, transparent 70%)" }} />
      </div>

      <div className={clsx(
        "relative w-full max-w-sm rounded-3xl overflow-hidden transition-all duration-500",
        "border border-violet-500/30",
        confirmed ? "scale-95 opacity-0" : "scale-100 opacity-100"
      )}
        style={{ background: "linear-gradient(160deg, #1a1030 0%, #0f0a1a 100%)" }}
      >
        {/* Top accent */}
        <div className="h-1 w-full" style={{ background: "linear-gradient(90deg, #7c3aed, #06b6d4, #7c3aed)" }} />

        <div className="p-6 space-y-5">

          {/* Header */}
          <div className="text-center space-y-2">
            <div className="flex justify-center mb-3">
              <div className="relative">
                {/* Hexagonal icon */}
                <svg width="64" height="64" viewBox="0 0 64 64" fill="none">
                  <path d="M32 4 L56 18 L56 46 L32 60 L8 46 L8 18 Z"
                    fill="rgba(124,58,237,0.15)" stroke="#7c3aed" strokeWidth="1.5" />
                  <path d="M32 14 L46 22 L46 42 L32 50 L18 42 L18 22 Z"
                    fill="rgba(6,182,212,0.1)" stroke="#06b6d4" strokeWidth="1" />
                  <text x="32" y="37" textAnchor="middle" fontSize="18" fill="#a78bfa" fontWeight="900">!</text>
                </svg>
                {/* Glow */}
                <div className="absolute inset-0 rounded-full blur-xl opacity-30"
                  style={{ background: "radial-gradient(circle, #7c3aed, transparent)" }} />
              </div>
            </div>

            <h2 className="text-lg font-black text-white">Willkommen bei StudoX Flow</h2>
            <p className="text-xs text-gray-400 leading-relaxed">
              Du verwendest diesen Code zum ersten Mal.{" "}
              <span className="text-amber-300 font-bold">Deine Daten werden jetzt dauerhaft gespeichert.</span>
            </p>
          </div>

          {/* Data display */}
          <div className="space-y-2">
            <p className="text-[10px] font-black text-gray-500 uppercase tracking-widest px-1">Deine Registrierungsdaten</p>
            <DataRow label="Name"  value={user?.name} />
            <DataRow label="PLZ"   value={user?.plz} />
            <DataRow label="Rolle" value={roleLabel} />
          </div>

          {/* Warning box */}
          <div className="bg-amber-950/30 border border-amber-800/40 rounded-2xl p-4 space-y-2">
            <div className="flex items-start gap-2.5">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" strokeWidth="2" stroke="#fbbf24"
                strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0 mt-0.5">
                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
              </svg>
              <div>
                <p className="text-xs font-black text-amber-300 mb-1">Wichtig: Daten sind endgültig</p>
                <p className="text-[11px] text-amber-200/70 leading-relaxed">
                  Name und PLZ werden permanent mit diesem Code verknüpft.
                  Bei einem Tippfehler muss ein neuer Code ausgestellt werden.
                  Stelle sicher, dass alles korrekt ist!
                </p>
              </div>
            </div>
          </div>

          {/* Checkbox confirm */}
          <button
            onClick={() => { sfxClick(); setChecked(!checked); }}
            className="w-full flex items-start gap-3 text-left group"
          >
            <div className={clsx(
              "flex-shrink-0 w-5 h-5 rounded-md border-2 flex items-center justify-center mt-0.5 transition-all duration-200",
              checked
                ? "bg-violet-600 border-violet-500"
                : "bg-transparent border-gray-600 group-hover:border-violet-500/60"
            )}>
              {checked && (
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" strokeWidth="3"
                  stroke="white" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
              )}
            </div>
            <span className={clsx("text-xs leading-relaxed transition-colors", checked ? "text-gray-300" : "text-gray-500")}>
              Ich bestätige, dass mein Name{" "}
              <span className="font-bold text-white">„{user?.name}"</span>{" "}
              und meine PLZ{" "}
              <span className="font-bold text-white">„{user?.plz}"</span>{" "}
              korrekt sind und verstehe, dass diese Daten nicht mehr geändert werden können.
            </span>
          </button>

          {/* Confirm button */}
          <button
            onClick={handleConfirm}
            disabled={!checked}
            className={clsx(
              "w-full py-3.5 rounded-xl font-black text-sm transition-all duration-300 relative overflow-hidden",
              checked
                ? "text-white shadow-lg shadow-violet-900/40 hover:scale-[1.02] active:scale-[0.98]"
                : "opacity-30 cursor-not-allowed text-gray-400 bg-nexus-s2 border border-nexus-border"
            )}
            style={checked ? {
              background: "linear-gradient(135deg, #7c3aed 0%, #2563eb 50%, #06b6d4 100%)"
            } : {}}
          >
            {checked && (
              <div className="absolute inset-0 opacity-0 hover:opacity-100 transition-opacity"
                style={{ background: "linear-gradient(135deg, #6d28d9, #1d4ed8, #0891b2)" }} />
            )}
            <span className="relative">Daten bestätigen & starten</span>
          </button>
        </div>
      </div>
    </div>
  );
}
